import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sites } from '@/mocks/sites';
import { categories } from '@/mocks/categories';
import SectionHeading from '@/components/base/SectionHeading';

const featured = sites.filter((site) => site.featured);

function categoryName(id: string) {
  return categories.find((category) => category.id === id)?.name ?? '精选推荐';
}

export default function FeaturedCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (next: number) => {
    setIndex((next + featured.length) % featured.length);
  };

  if (featured.length === 0) return null;

  return (
    <section className="w-full px-4 pt-12 md:px-8 md:pt-16">
      <div className="mx-auto max-w-[1280px]">
        <SectionHeading
          icon="ri-fire-line"
          title="焦点推荐"
          subtitle="最近更新与热门精选网站，一键直达"
        />
        <div className="relative overflow-hidden rounded-lg border border-background-200">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {featured.map((site) => (
              <Link
                key={site.id}
                to={`/detail/${site.id}`}
                className="relative block h-[280px] w-full flex-shrink-0 md:h-[380px]"
              >
                <img
                  src={site.cover}
                  alt={`${site.name} 推荐封面`}
                  title={`${site.name} - 网址大全热门推荐`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-accent-500 px-2.5 py-0.5 text-xs font-medium text-background-50">
                      热门推荐
                    </span>
                    <span className="rounded-full bg-background-50/20 px-2.5 py-0.5 text-xs text-background-50 backdrop-blur">
                      {categoryName(site.categoryId)}
                    </span>
                  </div>
                  <h3 className="mt-3 font-heading text-xl font-bold text-background-50 md:text-3xl">{site.name}</h3>
                  <p className="mt-2 max-w-2xl text-xs leading-relaxed text-background-100 md:text-sm">
                    {site.description}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-300">
                    查看详情
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <button
            type="button"
            aria-label="上一张"
            onClick={() => goTo(index - 1)}
            className="absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/85 text-foreground-800 transition-colors hover:bg-background-50 md:left-6 md:h-10 md:w-10"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => goTo(index + 1)}
            className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/85 text-foreground-800 transition-colors hover:bg-background-50 md:right-6 md:h-10 md:w-10"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>

          <div className="absolute bottom-4 right-4 flex items-center gap-1.5 md:bottom-6 md:right-6">
            {featured.map((site, dotIndex) => (
              <button
                key={site.id}
                type="button"
                aria-label={`切换到第 ${dotIndex + 1} 张`}
                onClick={() => goTo(dotIndex)}
                className={`h-1.5 rounded-full transition-all ${
                  dotIndex === index ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-50/70'
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}